// Disconnect instrumentation, measurement only. A frozen renderer and a dropped
// network look identical to the relay; the client's own timer lateness tells them
// apart (DriftTracker). buildReconnectReport is the closed payload, sent once per
// reconnect after reconnectReportDelayMs. Pure: no I/O, no globals, no imports.

/** Upper bound for any duration we report (24h). Anything larger is a clock artifact. */
export const MAX_REPORT_MS = 86_400_000;

/** Largest close code the WebSocket wire format can carry (uint16). */
const MAX_CLOSE_CODE = 65_535;

/** A reconnect burst longer than this is a clock artifact, not an attempt count. */
const MAX_ATTEMPTS = 10_000;

const KNOWN_SURFACES = ["web", "desktop", "mobile"] as const;
type KnownSurface = (typeof KNOWN_SURFACES)[number];

/**
 * Max lateness (`now - lastTickAt - nominalMs`, clamped at 0) across ticks of a
 * fixed interval. The MAX, not the last or the mean: one freeze inside a healthy
 * connection is the signal. A backwards clock step clamps to 0.
 */
export class DriftTracker {
  private readonly nominalMs: number;
  private lastTickAt: number | null = null;
  private maxLateness = 0;

  constructor(nominalMs: number) {
    this.nominalMs = Number.isFinite(nominalMs) && nominalMs > 0 ? nominalMs : 0;
  }

  /** Record a tick at `now`. The first tick only seeds the baseline (no lateness yet). */
  tick(now: number): void {
    if (!Number.isFinite(now)) return;
    const previous = this.lastTickAt;
    this.lastTickAt = now;
    if (previous === null) return;
    const lateness = now - previous - this.nominalMs;
    if (!Number.isFinite(lateness) || lateness <= 0) return;
    const clamped = Math.min(lateness, MAX_REPORT_MS);
    if (clamped > this.maxLateness) this.maxLateness = clamped;
  }

  /** Largest (actual - nominal) lateness observed, clamped into 0..MAX_REPORT_MS. */
  maxDriftMs(): number {
    return this.maxLateness;
  }

  reset(): void {
    this.lastTickAt = null;
    this.maxLateness = 0;
  }
}

/** A tick later than this is a suspension: above Chrome's 1/min hidden-tab throttle. */
export const SUSPENDED_LATENESS_MS = 60_000;
export const SUSPENSION_TICK_MS = 10_000;

/** Time asleep in one outage: the larger of minute-late tick lateness and wall time `performance.now()` missed. */
export class SuspensionMeter {
  private wallStart: number | undefined;
  private monoStart = 0;
  private lastTickAt = 0;
  private lateSum = 0;

  constructor(private readonly nominalMs: number) {}

  get running(): boolean {
    return this.wallStart !== undefined;
  }

  start(wallNow: number, monoNow: number): void {
    this.wallStart = wallNow;
    this.monoStart = monoNow;
    this.lastTickAt = wallNow;
    this.lateSum = 0;
  }

  tick(wallNow: number): void {
    if (this.wallStart === undefined || !Number.isFinite(wallNow)) return;
    const lateness = wallNow - this.lastTickAt - this.nominalMs;
    this.lastTickAt = wallNow;
    if (lateness >= SUSPENDED_LATENESS_MS) this.lateSum += lateness;
  }

  asleepMs(wallNow: number, monoNow: number): number {
    if (this.wallStart === undefined) return 0;
    this.tick(wallNow);
    const unseen = wallNow - this.wallStart - (monoNow - this.monoStart);
    return clampDuration(Math.max(this.lateSum, unseen), MAX_REPORT_MS);
  }

  stop(): void {
    this.wallStart = undefined;
    this.lateSum = 0;
  }
}

export interface ReconnectReportInput {
  downMs: number;
  driftMs: number;
  asleepMs: number;
  attempts: number;
  sawClose: boolean;
  closeCode: number | null;
  surface: string;
}

export interface ReconnectReportBody {
  downMs: number;
  driftMs: number;
  asleepMs: number;
  attempts: number;
  sawClose: boolean;
  closeCode: number | null;
  surface: "web" | "desktop" | "mobile" | "unknown";
}

function clampDuration(value: number, max: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return 0;
  const rounded = Math.round(value);
  if (rounded <= 0) return 0;
  return rounded > max ? max : rounded;
}

function normalizeSurface(value: string): ReconnectReportBody["surface"] {
  return (KNOWN_SURFACES as readonly string[]).includes(value)
    ? (value as KnownSurface)
    : "unknown";
}

/**
 * The closed payload: seven named fields, clamped, built FRESH from named reads and
 * never spread from the input, so an extra input property can never ride along
 * into the body. The no-egress guarantee: numbers, one boolean, one enum.
 */
export function buildReconnectReport(input: ReconnectReportInput): ReconnectReportBody {
  const sawClose = input.sawClose === true;
  const rawCode = input.closeCode;
  const closeCode =
    sawClose && typeof rawCode === "number" && Number.isFinite(rawCode)
      ? Math.min(Math.max(Math.round(rawCode), 0), MAX_CLOSE_CODE)
      : null;
  return {
    downMs: clampDuration(input.downMs, MAX_REPORT_MS),
    driftMs: clampDuration(input.driftMs, MAX_REPORT_MS),
    asleepMs: clampDuration(input.asleepMs, MAX_REPORT_MS),
    attempts: clampDuration(input.attempts, MAX_ATTEMPTS),
    sawClose,
    closeCode,
    surface: normalizeSurface(input.surface),
  };
}

/** Minimum wait before a report is POSTed, and the jitter window added on top. */
export const REPORT_DELAY_MIN_MS = 5_000;
export const REPORT_DELAY_JITTER_MS = 25_000;

/**
 * How long to hold a report before sending it: 5s + up to 25s of jitter. After a
 * relay restart every client reconnects at once and each reconnect already costs
 * an auth DB read; spreading the reports keeps them off that peak.
 */
export function reconnectReportDelayMs(random: () => number = Math.random): number {
  const r = random();
  const unit = Number.isFinite(r) ? Math.min(Math.max(r, 0), 1) : 0;
  return REPORT_DELAY_MIN_MS + Math.floor(unit * REPORT_DELAY_JITTER_MS);
}
